import React, { useState, useCallback, useEffect } from "react";

export function WPRSSSlide({ slide, width, height, isManuallyPaused, manualControlActive }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePreviousRssItem = (e) => {
    e.stopPropagation(); // Prevent DisplayChannel's mousedown listener from firing if not desired
    setActiveIndex(prev => (prev - 1 + divs.length) % divs.length);
  };

  const handleNextRssItem = (e) => {
    e.stopPropagation(); // Prevent DisplayChannel's mousedown listener from firing if not desired
    setActiveIndex(prev => (prev + 1) % divs.length);
  };
  var parser = new DOMParser();
  var htmlDoc = parser.parseFromString(slide.wp_rss_url, "text/html");

  const nodes = Array.from(htmlDoc.getElementsByTagName("body")[0].childNodes);
  // If wp_rss_items_to_display is 0, show all items (all childNodes of body, skipping the first one)
  const numItemsToDisplay = slide.wp_rss_items_to_display;
  const divs = numItemsToDisplay === 0 
                 ? nodes.slice(1) 
                 : nodes.slice(1, numItemsToDisplay + 1);

  const activeStyles = {
    position: "absolute",
    opacity: "1",
    transition: "opacity 0.5s",
    width: "inherit",
    height: "inherit",
  };

  const inactiveStyles = { ...activeStyles, opacity: "0" };

  // Calculate itemDisplayDuration (duration for each individual RSS item)
  const DEFAULT_ITEM_DURATION_MS = 5000; // Default to 5 seconds per item
  let calculatedItemDurationMs;

  const transitionDelaySec = parseFloat(slide.wp_rss_transition_delay);
  const totalSlideDurationSec = parseFloat(slide.duration);

  if (transitionDelaySec > 0) {
    calculatedItemDurationMs = transitionDelaySec * 1000;
  } else if (divs.length > 0 && totalSlideDurationSec > 0) {
    calculatedItemDurationMs = (totalSlideDurationSec / divs.length) * 1000;
  } else if (divs.length > 0) { // Has items, but no specific durations given
    calculatedItemDurationMs = DEFAULT_ITEM_DURATION_MS;
  } else { // No divs to display, or invalid/zero total duration.
    // Use total slide duration if valid, otherwise default for the (potentially empty) slide.
    calculatedItemDurationMs = totalSlideDurationSec > 0
                               ? totalSlideDurationSec * 1000
                               : DEFAULT_ITEM_DURATION_MS;
  }
  const itemDisplayDuration = calculatedItemDurationMs;

  //once loaded, set the timer to switch slides.
  const switchSlides = useCallback(
    (current) => {
      if (divs.length === 0) return; // Do nothing if no divs
      const next = (current + 1 >= divs.length) ? 0 : current + 1;
      setActiveIndex(next);
      // Only set timeout for the *next* transition if there's more than one slide 
      // AND we are not paused under manual control.
      if (divs.length > 1) {
        if (manualControlActive && isManuallyPaused) {
          // Paused: do not schedule the next automatic slide change.
          return;
        }
        // Not paused or manual control not active: schedule next change.
        setTimeout(() => switchSlides(next), itemDisplayDuration);
      }
    },
    [divs.length, itemDisplayDuration, setActiveIndex, isManuallyPaused, manualControlActive] 
  );

  useEffect(() => {
    let timerId;
    // Conditions to start/resume the slideshow timer
    const shouldRunSlideshow = slide.rss_type !== "notransition" && divs.length > 1;
    // Timer can run if manual control is not active, OR if it is active but not currently paused.
    const canRunTimer = !manualControlActive || (manualControlActive && !isManuallyPaused);

    if (shouldRunSlideshow && canRunTimer) {
      // Start the timer to transition from the current activeIndex
      timerId = setTimeout(() => switchSlides(activeIndex), itemDisplayDuration);
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId); // Cleanup timer on unmount or when dependencies change
      }
    };
  }, [slide.rss_type, divs.length, itemDisplayDuration, switchSlides, activeIndex, isManuallyPaused, manualControlActive]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}> {/* Ensure positioning context for arrows */}
      {divs.map((item, idx) => {
        return (
          <div
            style={idx === activeIndex ? activeStyles : inactiveStyles}
            key={idx}
            dangerouslySetInnerHTML={{ __html: item.outerHTML }}
          ></div>
        );
      })}
      {/* Sub-slide Navigation Arrows for RSS Items */}
      {manualControlActive && divs.length > 1 && (
        <>
          <button 
            onClick={handlePreviousRssItem} 
            style={{
              position: 'absolute',
              left: '5px',
              bottom: '5px',
              zIndex: 10, // Ensure arrows are on top of slide content but below main slide arrows
              background: 'rgba(0,0,0,0.4)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              fontSize: '16px',
              cursor: 'pointer',
              lineHeight: '30px', // Center arrow vertically
              padding: '0'
            }}
          >
            &#x2190; {/* Left Arrow */}
          </button>
          <button 
            onClick={handleNextRssItem} 
            style={{
              position: 'absolute',
              right: '5px',
              bottom: '5px',
              zIndex: 10,
              background: 'rgba(0,0,0,0.4)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              fontSize: '16px',
              cursor: 'pointer',
              lineHeight: '30px', // Center arrow vertically
              padding: '0'
            }}
          >
            &#x2192; {/* Right Arrow */}
          </button>
        </>
      )}
    </div>
  );
}

export default WPRSSSlide;
